import { e2eeRoomId, e2eeUserId, MAX_EMOJI_LENGTH, openVault, sealVault, userIdFromE2ee, type DeviceDto, type MegolmContent, type MessageDto, type VaultContents, type VaultEnvelope } from '@dijitalapp/shared';
import type * as CryptoModule from '@matrix-org/matrix-sdk-crypto-wasm';
import { api, ApiError } from './api';
import { KeyRecovery, type KeyStatus, type MissingSession } from './e2eeRecovery';
import { browserTabLockEnv, TabLock } from './e2eeTabLock';
import type { E2eeFileMeta } from './outbox';

/**
 * Browser side of end-to-end encryption (ADR-014). All cryptography happens inside matrix-sdk-crypto
 * (vodozemac, compiled to WebAssembly); this module moves its requests to our key-distribution API,
 * feeds the answers back, and drives the user-facing flows (identity, verification, backup).
 *
 * Trust model: each person has a cross-signing identity (master key) that signs their devices. Room keys
 * are shared only with devices signed by their owner's identity; messages are only accepted from such
 * devices. People are "verified" by comparing emoji or a safety code, which signs their identity.
 */

type Crypto = typeof CryptoModule;
type Machine = CryptoModule.OlmMachine;
type Outgoing = { id?: string; type: number; body: string; event_type?: string; txn_id?: string; version?: string };

const PASS_KEY = (deviceId: string) => `dijitalapp-e2ee-pass:${deviceId}`;
const SINCE_KEY = (deviceId: string) => `dijitalapp-e2ee-since:${deviceId}`;
const BACKUP_SECRET = 'm.megolm_backup.v1';
/** Which devices already got a chat's earlier keys from this device (see forwardHistory). */
// v2: the first version addressed packets wrongly, so every device it recorded as served never got its keys.
const FORWARDED_KEY = (deviceId: string) => `dijitalapp-e2ee-forwarded-v2:${deviceId}`;
/** Our own to-device packet carrying exported room keys (Olm-encrypted, like everything to-device). */
const ROOM_KEYS_EVENT = 'io.dijitalapp.room_keys';
/**
 * Asks for a missing room key again (Olm-encrypted, device to device). Sent to the device that wrote the message
 * and to my own other devices; see answerKeyRequests for who gets an answer.
 */
const KEY_REQUEST_EVENT = 'io.dijitalapp.room_key_request';
/** Sessions whose key came straight from the device that created them (see importForwardedKeys). */
const AUTHENTIC_KEY = (deviceId: string) => `dijitalapp-e2ee-authentic:${deviceId}`;
const AUTHENTIC_MAX = 2000;
const OTK_REFRESHED = (deviceId: string) => `dijitalapp-e2ee-otk-refreshed-v1:${deviceId}`;
/** A device asking for the same key again within this time is not answered twice. */
const ANSWER_EVERY_MS = 20_000;
/** Keys per packet: the server takes 64 KB per request, an exported session is about 1 KB. */
const FORWARD_BATCH = 20;
const BACKUP_ALGORITHM = 'm.megolm_backup.v1.curve25519-aes-sha2';

function storeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function storeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the store then only lives for this tab */
  }
}

/**
 * Passphrase for the library's encrypted IndexedDB store. It lives in the same browser profile, so it
 * only protects against raw copies of the database, not against someone who controls this browser.
 */
function storePassphrase(deviceId: string): string {
  let pass = storeGet(PASS_KEY(deviceId));
  if (!pass) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    pass = btoa(String.fromCharCode(...bytes));
    storeSet(PASS_KEY(deviceId), pass);
  }
  return pass;
}

/** Splits a key into groups of four characters so two people can read it to each other. */
export function formatCode(key: string): string {
  return (key.match(/.{1,4}/g) ?? []).join(' ');
}

export type PeerTrust = 'verified' | 'unverified' | 'changed';
export type VerificationPhase = 'requested' | 'ready' | 'qr-show' | 'qr-scanned' | 'qr-waiting' | 'emoji' | 'confirmed' | 'done' | 'cancelled';

export interface VerificationView {
  flowId: string;
  /** DijitalApp user id of the other side (ourselves for a device-to-device self verification). */
  userId: string;
  self: boolean;
  incoming: boolean;
  phase: VerificationPhase;
  emoji: { symbol: string; description: string }[];
  /** Our QR code while we show it: module grid, `size` × `size`. */
  qr: { size: number; modules: Uint8ClampedArray } | null;
  reason: string | null;
}

interface Flow {
  request: CryptoModule.VerificationRequest;
  sas: CryptoModule.Sas | null;
  /** QR verification: the code we show, or the one we scanned. */
  qr: CryptoModule.Qr | null;
  qrShown: boolean;
  incoming: boolean;
  self: boolean;
  confirmed: boolean;
  finished: boolean;
}

/** A shared contact card inside an encrypted message. */
export const CONTACT_MSGTYPE = 'app.dijitalapp.contact';
/** A poll or an event in an encrypted chat: the whole thing travels inside the ciphertext. */
export const POLL_MSGTYPE = 'app.dijitalapp.poll';
export const EVENT_MSGTYPE = 'app.dijitalapp.event';
/** One person's vote / event answer (and, from the creator, "closed"), pointing at the poll or event. */
const ANSWER_TYPE = 'app.dijitalapp.answer';

export interface EncryptedPoll {
  question: string;
  options: string[];
  multiple: boolean;
  anonymous: boolean;
}
export interface EncryptedEvent {
  title: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  description: string | null;
}
export interface EncryptedAnswer {
  vote?: number[];
  rsvp?: 'going' | 'maybe' | 'no' | null;
  /** From the creator only: the poll is closed / the event is called off. */
  closed?: boolean;
}

/** Decrypted room event content (`m.text`, `m.file`, `m.image`, `m.audio`, `m.video`). */
export interface DecryptedContent {
  /** CONTACT_MSGTYPE: who is shared. */
  contact?: { id: string; username: string; displayName: string };
  msgtype?: string;
  body?: string;
  /** Reactions point at the message they belong to. */
  'm.relates_to'?: { rel_type?: string; event_id?: string };
  info?: { mimetype?: string; size?: number; duration?: number; round?: boolean; waveform?: number[]; sticker?: boolean; thumbnail_data?: string; w?: number; h?: number };
  file?: Record<string, unknown> & { url?: string };
  /** A photo's or file's caption (body is the file's name here). Older apps ignore it and show the file only. */
  caption?: string;
  /** m.location: "geo:lat,lng;u=accuracy". */
  geo_uri?: string;
  poll?: EncryptedPoll;
  event?: EncryptedEvent;
  answer?: EncryptedAnswer;
}
export interface DecryptedMessage {
  content: DecryptedContent;
  unverifiedSender: boolean;
}

/** Attachment ids travel as Matrix content URIs inside the encrypted content. */
export const fileUri = (attachmentId: string) => `mxc://dijitalapp.com/${attachmentId}`;
export const attachmentIdOf = (uri: string | undefined) => (uri?.startsWith('mxc://dijitalapp.com/') ? uri.slice('mxc://dijitalapp.com/'.length) : null);

export class E2ee {
  private chain: Promise<unknown> = Promise.resolve();
  private since: string | undefined;
  private plaintext = new Map<string, Promise<DecryptedMessage | null>>();
  /** Recipients of the current group session per conversation; any departure starts a new session. */
  private sessionMembers = new Map<string, Set<string>>();
  private listeners = new Set<() => void>();
  private flows = new Map<string, Flow>();
  private watched = new Set<string>();
  /** Bumped on every change the UI shows (new keys, trust, verification progress). */
  version = 0;
  /** This device is signed by its owner's identity (can read and be trusted). */
  deviceVerified = false;
  /** Whether the account has an encryption vault (a backup password); null until known. */
  hasVault: boolean | null = null;
  /** This device holds every key the vault keeps, so it can set or change the backup password. */
  canSealVault = false;
  /** Messages whose key is missing: looked for with growing pauses (waiting), then failed. */
  private readonly recovery: KeyRecovery;
  /** Cache keys of messages that failed for want of a session's key (dropped when the key arrives). */
  private missingBySession = new Map<string, Set<string>>();
  /** Session id → user whose own signed device sent us the key (see importForwardedKeys). */
  private authentic: Map<string, string>;
  private answered = new Map<string, number>();
  /** Whether a user is a member of a chat (set by the chat store); key requests from others are answered only then. */
  private memberCheck: ((conversationId: string, userId: string) => Promise<boolean>) | null = null;
  /** Another tab of this device has the crypto right now; operations wait until this tab gets it back. */
  private parked = false;
  private stopped = false;
  private pendingSync: Promise<void> | null = null;
  private changeQueued = false;

  private constructor(
    private readonly lib: Crypto,
    private machine: Machine,
    readonly userId: string,
    readonly deviceId: string,
    private readonly lock: TabLock,
    private readonly storeName: string,
    private readonly storePass: string,
  ) {
    this.since = storeGet(SINCE_KEY(deviceId)) ?? undefined;
    this.watched.add(userId);
    this.authentic = readAuthentic(deviceId);
    this.recovery = new KeyRecovery({
      now: () => Date.now(),
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
      attempt: (s, how) => this.searchKey(s, how),
      changed: () => this.changed(),
    });
    this.watchKeys();
    lock.onYield = async () => {
      await this.beforeHandOver?.().catch(() => undefined);
      await this.exclusive(() => this.handOver(false));
    };
    lock.onLost = () => {
      // Taken without a hand-over (this tab was frozen): stop touching the store at once, then wait for our turn.
      this.parked = true;
      try {
        this.machine.close();
      } catch {
        /* already closed */
      }
      void this.exclusive(() => this.handOver(true));
    };
  }

  /**
   * Starts this device's crypto. Only one tab of the device runs it at a time (see e2eeTabLock): in a second tab
   * this waits until that tab is brought to the front and the first one hands over.
   */
  static async start(userId: string, retried = false): Promise<E2ee | null> {
    if (typeof WebAssembly === 'undefined' || typeof indexedDB === 'undefined') return null;
    const { devices } = await api<{ devices: DeviceDto[] }>('GET', '/devices');
    const deviceId = devices.find((d) => d.current)?.id;
    if (!deviceId) return null;
    const lib = await import('@matrix-org/matrix-sdk-crypto-wasm');
    await lib.initAsync();
    const storeName = `dijitalapp-e2ee:${deviceId}`;
    const lock = new TabLock(storeName, browserTabLockEnv());
    await lock.acquire();
    const pass = storePassphrase(deviceId);
    let machine: Machine;
    try {
      machine = await lib.OlmMachine.initialize(new lib.UserId(e2eeUserId(userId)), new lib.DeviceId(deviceId), storeName, pass);
    } catch (err) {
      lock.close();
      throw err;
    }
    const e = new E2ee(lib, machine, userId, deviceId, lock, storeName, pass);
    try {
      await e.sync();
    } catch (err) {
      // The crypto store was lost (evicted, cleared) while the session lived on: the server refuses new
      // identity keys for this device id. Start over as a new device, once.
      const fields = err instanceof ApiError && err.code === 'VALIDATION_FAILED' ? err.fields : undefined;
      await e.stop();
      if (!retried && fields && (fields.device_keys === 'immutable' || fields.device_keys === 'mismatch')) {
        await api('POST', '/e2ee/device/reset');
        return E2ee.start(userId, true);
      }
      throw err;
    }
    await e.exclusive(() => e.ensureIdentity());
    // Once per device: earlier versions could run the crypto in two tabs at once, leaving one-time keys on the
    // server that this device's store does not hold (every room key sent with one was lost). Fresh ones replace them.
    if (!storeGet(OTK_REFRESHED(deviceId))) {
      await e
        .exclusive(async () => {
          await api('POST', '/e2ee/keys/one-time/refresh');
          await e.pull();
          storeSet(OTK_REFRESHED(deviceId), '1');
        })
        .catch(() => undefined);
    }
    return e;
  }

  /** Signed out / the app goes away: lets another tab have the crypto and stops looking for keys. */
  stop(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    this.stopped = true;
    this.recovery.stop();
    // Waiting for another tab to hand over: stop waiting (the queued work below then runs and ends).
    if (this.parked) this.lock.close();
    return this.exclusive(async () => {
      this.parked = true;
      try {
        this.machine.close();
      } catch {
        /* already closed */
      }
      this.lock.close();
    });
  }

  /** This tab runs the crypto now (false while another tab of this device has it, or after stop). */
  get active(): boolean {
    return !this.parked && !this.stopped;
  }

  private beforeHandOver: (() => Promise<void>) | null = null;

  /** Runs before this tab hands the crypto to another tab (the chat store lets queued messages go out first). */
  setBeforeHandOver(fn: () => Promise<void>): void {
    this.beforeHandOver = fn;
  }

  /** Who may get a key again on request besides my own devices: current members of the chat. */
  setMemberCheck(fn: (conversationId: string, userId: string) => Promise<boolean>): void {
    this.memberCheck = fn;
  }

  /**
   * Another tab in front asked for the crypto (or took it): close it here, let that tab have it, and wait for our
   * turn. Runs inside `exclusive`, so nothing else touches the store meanwhile; queued work continues after.
   */
  private async handOver(alreadyClosed: boolean): Promise<void> {
    if (this.stopped) return;
    this.parked = true;
    if (!alreadyClosed) {
      try {
        this.machine.close();
      } catch {
        /* already closed */
      }
    }
    this.flows.clear();
    this.lock.unlock();
    this.changed();
    await this.lock.acquire();
    if (this.stopped) return;
    this.machine = await this.lib.OlmMachine.initialize(new this.lib.UserId(e2eeUserId(this.userId)), new this.lib.DeviceId(this.deviceId), this.storeName, this.storePass);
    this.watchKeys();
    // The other tab moved on meanwhile: its acknowledged packets, keys and trust are in the store.
    this.since = storeGet(SINCE_KEY(this.deviceId)) ?? undefined;
    this.authentic = readAuthentic(this.deviceId);
    this.parked = false;
    this.dropFailures();
    await this.pull().catch(() => undefined);
    await this.refreshDeviceTrust().catch(() => undefined);
    this.recovery.nudge();
    this.changed();
  }

  /** Room keys stored by any path (to-device, backup, re-share, import) retry the messages that waited for them. */
  private watchKeys() {
    this.machine.registerRoomKeyUpdatedCallback(async (infos) => {
      for (const info of infos) this.keyArrived(info.sessionId);
    });
  }

  private keyArrived(sessionId: string) {
    const keys = this.missingBySession.get(sessionId);
    this.missingBySession.delete(sessionId);
    keys?.forEach((k) => this.plaintext.delete(k));
    const was = this.recovery.resolved(sessionId);
    if (keys || was) this.changedSoon();
  }

  /** Forgets every "could not decrypt", so the next look tries again. */
  private dropFailures() {
    for (const keys of this.missingBySession.values()) keys.forEach((k) => this.plaintext.delete(k));
    this.missingBySession.clear();
    for (const [id, p] of this.plaintext) void p.then((v) => v === null && this.plaintext.get(id) === p && this.plaintext.delete(id));
  }

  private changedSoon() {
    if (this.changeQueued) return;
    this.changeQueued = true;
    queueMicrotask(() => {
      this.changeQueued = false;
      this.changed();
    });
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed() {
    this.version += 1;
    this.listeners.forEach((l) => l());
  }

  /** Runs crypto operations one at a time: the state machine must not process requests concurrently. */
  private exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(fn, fn);
    this.chain = run.catch(() => undefined);
    return run;
  }

  private uid(id: string) {
    return new this.lib.UserId(e2eeUserId(id));
  }

  /**
   * Signs a text with this device's own Ed25519 key (the one its owner's identity cross-signs): unpadded base64,
   * null when the library gives none. Used for the push-preview key (lib/pushPreview.ts), so senders can tell the
   * key is this device's and not one the server slipped in.
   */
  signText(text: string): Promise<string | null> {
    return this.exclusive(async () => {
      const signatures = await this.machine.sign(text);
      try {
        const all = JSON.parse(signatures.asJSON()) as Record<string, Record<string, string>>;
        const mine = all[e2eeUserId(this.userId)] ?? {};
        const sig = mine[`ed25519:${this.deviceId}`];
        return typeof sig === 'string' ? sig : null;
      } finally {
        signatures.free();
      }
    });
  }

  /**
   * The Ed25519 keys of these people's devices that their owner's identity signed (the devices room keys are
   * shared with: identity-based sharing), by "userId/deviceId". A device missing here gets no encrypted preview.
   */
  signedDeviceKeys(userIds: string[]): Promise<Map<string, string>> {
    return this.exclusive(async () => {
      const out = new Map<string, string>();
      await this.track(userIds);
      for (const u of userIds) {
        const devices = await this.machine.getUserDevices(this.uid(u));
        for (const d of devices.devices()) {
          const key = d.ed25519Key?.toBase64();
          if (key && d.isCrossSignedByOwner() && !d.isBlacklisted() && !d.isDeleted()) out.set(`${u}/${d.deviceId.toString()}`, key);
        }
      }
      return out;
    });
  }

  // ---------- transport ----------

  private async send(r: Outgoing) {
    const { RequestType } = this.lib;
    const body = JSON.parse(r.body) as unknown;
    let response: unknown = {};
    switch (r.type) {
      case RequestType.KeysUpload:
        response = await api('POST', '/e2ee/keys/upload', body);
        break;
      case RequestType.KeysQuery:
        response = await api('POST', '/e2ee/keys/query', body);
        break;
      case RequestType.KeysClaim:
        response = await api('POST', '/e2ee/keys/claim', body);
        break;
      case RequestType.ToDevice:
        response = await api('PUT', `/e2ee/send-to-device/${encodeURIComponent(r.event_type ?? '')}/${encodeURIComponent(r.txn_id ?? '')}`, body);
        break;
      case RequestType.SignatureUpload:
        response = await api('POST', '/e2ee/keys/signatures/upload', body);
        break;
      case RequestType.KeysBackup:
        response = await api('PUT', `/e2ee/room-keys/keys?version=${encodeURIComponent(r.version ?? '')}`, body);
        break;
      default:
        break; // In-room events are not used: verification runs device to device.
    }
    if (r.id) await this.machine.markRequestAsSent(r.id, r.type, JSON.stringify(response ?? {}));
  }

  private async flush() {
    for (const r of await this.machine.outgoingRequests()) await this.send(r as Outgoing);
  }

  /**
   * Pulls pending packets and device-list changes; called on start, on reconnect and on `e2ee.sync`. Calls made
   * while one is still waiting its turn share it.
   */
  sync(): Promise<void> {
    if (this.pendingSync) return this.pendingSync;
    const run = this.exclusive(async () => {
      this.pendingSync = null;
      await this.pull();
    });
    this.pendingSync = run;
    return run;
  }

  private async pull(): Promise<void> {
    let received = 0;
    try {
      for (;;) {
        const res = await api<{
          next: string;
          more: boolean;
          to_device: { events: unknown[] };
          device_lists: { changed: string[] };
          device_one_time_keys_count: Record<string, number>;
          device_unused_fallback_key_types: string[];
        }>('GET', `/e2ee/sync${this.since ? `?since=${encodeURIComponent(this.since)}` : ''}`);
        const processed = await this.machine.receiveSyncChanges(
          JSON.stringify(res.to_device.events),
          new this.lib.DeviceLists(res.device_lists.changed.map((u) => new this.lib.UserId(u))),
          new Map(Object.entries(res.device_one_time_keys_count)),
          new Set(res.device_unused_fallback_key_types),
        );
        received += await this.importForwardedKeys(processed);
        // Only acknowledge after the machine has stored the packets.
        this.since = res.next;
        storeSet(SINCE_KEY(this.deviceId), res.next);
        received += res.to_device.events.length + res.device_lists.changed.length;
        await this.flush();
        await this.answerKeyRequests(processed).catch(() => undefined);
        if (!res.more) break;
      }
      await this.collectIncomingVerifications();
      await this.pumpVerifications();
      await this.receiveBackupSecret();
      await this.refreshDeviceTrust();
      await this.backup();
    } finally {
      // Also when a later step failed (a moment without network): what did arrive may make messages readable.
      // New keys retry their messages by themselves (watchKeys); this covers trust and device changes.
      if (received > 0) {
        this.dropFailures();
        this.changed();
      }
    }
  }

  // ---------- identity ----------

  /**
   * First device of a person: creates the cross-signing identity and the key backup (whose recovery key
   * is shown once). Later devices stay unverified until verified from another device.
   */
  private async ensureIdentity() {
    await this.machine.updateTrackedUsers([this.uid(this.userId)]);
    await this.flush();
    const own = await this.machine.getIdentity(this.uid(this.userId));
    const status = await this.machine.crossSigningStatus();
    if (!own && !status.hasMaster) {
      await this.bootstrap(false);
      await this.createBackup();
    } else {
      await this.enableExistingBackup();
    }
    await this.refreshDeviceTrust();
    await this.refreshVault();
    this.changed();
  }

  private async bootstrap(reset: boolean, password?: string) {
    const reqs = await this.machine.bootstrapCrossSigning(reset);
    if (reqs.uploadKeysRequest) await this.send(reqs.uploadKeysRequest as Outgoing);
    const signing = JSON.parse(reqs.uploadSigningKeysRequest.body) as Record<string, unknown>;
    await api('POST', '/e2ee/keys/device-signing/upload', password ? { ...signing, auth: { password } } : signing);
    await api('POST', '/e2ee/keys/signatures/upload', JSON.parse(reqs.uploadSignaturesRequest.body));
    await this.flush();
  }

  private async refreshDeviceTrust() {
    const own = await this.machine.getIdentity(this.uid(this.userId));
    const device = await this.machine.getDevice(this.uid(this.userId), new this.lib.DeviceId(this.deviceId));
    const verified = Boolean(own && device?.isCrossSignedByOwner());
    if (verified !== this.deviceVerified) {
      this.deviceVerified = verified;
      this.changed();
    }
  }

  /** The person's identity key, formatted for comparison ("safety code"); null if they have none yet. */
  async safetyCode(userId: string): Promise<string | null> {
    return this.exclusive(async () => {
      await this.track([userId]);
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) return null;
      const keys = (JSON.parse(identity.masterKey) as { keys: Record<string, string> }).keys;
      const key = Object.values(keys)[0];
      return key ? formatCode(key) : null;
    });
  }

  trustOf(userId: string): Promise<PeerTrust> {
    return this.exclusive(() => this.trustOfInner(userId));
  }

  private async trustOfInner(userId: string): Promise<PeerTrust> {
    const identity = await this.machine.getIdentity(this.uid(userId));
    if (!identity) return 'unverified';
    if (identity.hasVerificationViolation()) return 'changed';
    if (identity.isVerified()) return 'verified';
    if ('identityNeedsUserApproval' in identity && identity.identityNeedsUserApproval()) return 'changed';
    return 'unverified';
  }

  /** Users whose devices and verification requests we follow (conversation peers). */
  async track(userIds: string[]) {
    const fresh = userIds.filter((u) => !this.watched.has(u));
    fresh.forEach((u) => this.watched.add(u));
    await this.machine.updateTrackedUsers(userIds.map((u) => this.uid(u)));
    await this.flush();
  }

  /**
   * Signing someone's identity needs my user-signing key, which only the device that created my identity has
   * until my other devices receive it (after being verified). Without it, ask for it and explain what to do.
   */
  private async requireSigningKey(): Promise<void> {
    if ((await this.machine.crossSigningStatus()).hasUserSigning) return;
    await this.machine.requestMissingSecretsIfNeeded();
    await this.flush();
    throw new ApiError('E2EE_SIGNING_KEY_MISSING', 0);
  }

  /** After comparing the safety code in person: signs their identity. */
  verifyByCode(userId: string): Promise<void> {
    return this.exclusive(async () => {
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) throw new ApiError('E2EE_UNAVAILABLE', 0);
      await this.requireSigningKey();
      try {
        await this.send((await identity.verify()) as Outgoing);
      } catch (err) {
        throw cryptoError(err);
      }
      this.changed();
    });
  }

  /** The person reset their identity: accept the new one (withdrawing an old verification). */
  acceptIdentityChange(userId: string): Promise<void> {
    return this.exclusive(async () => {
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) return;
      if (identity.hasVerificationViolation()) await identity.withdrawVerification();
      else if ('pinCurrentMasterKey' in identity) await identity.pinCurrentMasterKey();
      this.changed();
    });
  }

  /**
   * Starts encryption over (no device left and the backup password forgotten): a new identity, a new backup
   * and a new vault under the new backup password. Old messages stay unreadable; peers see a security change.
   */
  resetIdentity(password: string, backupPassword: string): Promise<void> {
    return this.exclusive(async () => {
      await this.bootstrap(true, password);
      await this.createBackup();
      await this.refreshDeviceTrust();
      await this.refreshVault();
      await this.writeVault(backupPassword);
      this.changed();
    });
  }

  // ---------- verification (emoji / SAS, device to device) ----------

  private async collectIncomingVerifications() {
    for (const user of this.watched) {
      for (const request of this.machine.getVerificationRequests(this.uid(user))) {
        if (this.flows.has(request.flowId) || request.weStarted() || request.isDone() || request.isCancelled()) continue;
        this.addFlow(request, true);
      }
    }
  }

  private addFlow(request: Flow['request'], incoming: boolean) {
    const flow: Flow = { request, sas: null, qr: null, qrShown: false, incoming, self: request.isSelfVerification(), confirmed: false, finished: false };
    this.flows.set(request.flowId, flow);
    request.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
    this.changed();
  }

  /** Advances every flow by one step where the protocol (not the user) decides. */
  private async pumpVerifications() {
    for (const flow of this.flows.values()) {
      const { request } = flow;
      if (flow.finished || request.isCancelled()) continue;
      if (!flow.sas) {
        const v = request.getVerification();
        if (v instanceof this.lib.Sas) {
          flow.sas = v;
          this.watchSas(flow);
          if (!v.weStarted() && !v.hasBeenAccepted()) {
            const accept = v.accept();
            if (accept) await this.send(accept as Outgoing);
          }
        } else if (v instanceof this.lib.Qr && !flow.qr) {
          flow.qr = v;
          this.watchQr(flow);
        }
      }
      if ((request.isDone() || flow.sas?.isDone() || flow.qr?.isDone()) && !flow.finished) {
        flow.finished = true;
        await this.afterVerified(flow);
      }
    }
    this.changed();
  }

  private watchQr(flow: Flow) {
    flow.qr?.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
  }

  private watchSas(flow: Flow) {
    flow.sas?.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
  }

  private async afterVerified(flow: Flow) {
    if (flow.self) {
      // Our own new device: fetch the identity's signing keys and the backup key from the verified one.
      await this.machine.requestMissingSecretsIfNeeded();
      await this.flush();
    } else {
      // Their device was verified with emoji; if it is signed by their identity, that identity is
      // authenticated too (the library checks the signature chain), so we sign it.
      const other = flow.request.otherUserId;
      const otherDevice = flow.request.otherDeviceId;
      const device = otherDevice ? await this.machine.getDevice(other, otherDevice) : undefined;
      const identity = await this.machine.getIdentity(other);
      if (device?.isVerified() && device.isCrossSignedByOwner() && identity && !identity.isVerified()) {
        await this.send((await identity.verify()) as Outgoing);
      }
    }
    await this.flush();
    await this.refreshDeviceTrust();
  }

  /** Starts emoji verification with every signed device of a person; the first one to answer wins. */
  verifyUser(userId: string): Promise<void> {
    return this.exclusive(async () => {
      // The end of an emoji check signs their identity: needs the same key as the safety code.
      await this.requireSigningKey();
      await this.track([userId]);
      const devices = await this.machine.getUserDevices(this.uid(userId));
      for (const device of devices.devices()) {
        if (!device.isCrossSignedByOwner()) continue;
        const [request, out] = device.requestVerification(this.methods());
        await this.send(out as Outgoing);
        this.addFlow(request, false);
      }
    });
  }

  /** Verifies this (new) device with another of our own devices. */
  verifyThisDevice(): Promise<void> {
    return this.exclusive(async () => {
      await this.track([this.userId]);
      const own = await this.machine.getIdentity(this.uid(this.userId));
      if (!own || !('trustsOurOwnDevice' in own)) throw new Error('no identity');
      const [request, out] = await own.requestVerification(this.methods());
      await this.send(out as Outgoing);
      this.addFlow(request, false);
    });
  }

  verifications(): VerificationView[] {
    const views: VerificationView[] = [];
    for (const [flowId, flow] of this.flows) {
      const { request, sas } = flow;
      const qr = flow.qr;
      const phase: VerificationPhase = request.isCancelled() || sas?.isCancelled() || qr?.isCancelled()
        ? 'cancelled'
        : flow.finished
          ? 'done'
          : qr?.hasBeenScanned() && !qr.hasBeenConfirmed()
            ? 'qr-scanned'
            : qr?.reciprocated() || qr?.hasBeenConfirmed()
              ? 'qr-waiting'
              : flow.qrShown
                ? 'qr-show'
                : flow.confirmed
            ? 'confirmed'
            : sas?.emoji()
              ? 'emoji'
              : request.isReady()
                ? 'ready'
                : 'requested';
      views.push({
        flowId,
        userId: userIdFromE2ee(request.otherUserId.toString()) ?? '',
        self: flow.self,
        incoming: flow.incoming,
        phase,
        emoji: (sas?.emoji() ?? []).map((e: CryptoModule.Emoji) => ({ symbol: e.symbol, description: e.description })),
        qr: phase === 'qr-show' && qr ? qrGrid(qr) : null,
        reason: request.cancelInfo?.reason() ?? null,
      });
    }
    return views;
  }

  acceptVerification(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      const out = flow?.request.acceptWithMethods(this.methods());
      if (out) await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  private methods(): CryptoModule.VerificationMethod[] {
    const m = this.lib.VerificationMethod;
    return [m.SasV1, m.QrCodeShowV1, m.QrCodeScanV1, m.ReciprocateV1];
  }

  /** Compare emoji (either side may start it once the request is accepted). */
  startEmoji(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow || flow.sas) return;
      const started = await flow.request.startSas();
      if (!started) throw new Error('emoji verification could not start');
      flow.sas = started[0];
      flow.qrShown = false;
      await this.send(started[1] as Outgoing);
      this.watchSas(flow);
      await this.pumpVerifications();
    });
  }

  /** Show our QR code for the other device to scan. */
  showQr(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const qr = await flow.request.generateQrCode();
      if (!qr) throw new Error('no QR for this verification');
      flow.qr = qr;
      flow.qrShown = true;
      this.watchQr(flow);
      this.changed();
    });
  }

  /** We scanned the other device's QR code: the library checks it and we tell the other side. */
  scanQr(flowId: string, bytes: Uint8ClampedArray): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const qr = await flow.request.scanQrCode(this.lib.QrCodeScan.fromBytes(bytes));
      flow.qr = qr;
      flow.qrShown = false;
      this.watchQr(flow);
      const out = qr.reciprocate();
      // No reciprocation means the code did not match this verification: stop instead of waiting forever.
      if (!out) {
        const cancel = qr.cancel();
        if (cancel) await this.send(cancel as Outgoing);
        throw new Error('QR code did not match');
      }
      await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  /** The other device scanned our code and shows success: we confirm, which completes verification. */
  confirmQrScanned(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      const out = flow?.qr?.confirmScanning();
      if (out) await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  /** The person says the emoji match on both screens. */
  confirmEmoji(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow?.sas) return;
      flow.confirmed = true;
      for (const r of await flow.sas.confirm()) await this.send(r as Outgoing);
      await this.pumpVerifications();
    });
  }

  cancelVerification(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const out = flow.sas?.cancel() ?? flow.qr?.cancel() ?? flow.request.cancel();
      if (out) await this.send(out as Outgoing);
      this.changed();
    });
  }

  dismissVerification(flowId: string) {
    this.flows.delete(flowId);
    this.changed();
  }

  // ---------- key backup ----------

  private async currentBackup(): Promise<{ version: string; auth_data: { public_key: string } } | null> {
    try {
      return await api('GET', '/e2ee/room-keys/version');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  private async createBackup() {
    const key = this.lib.BackupDecryptionKey.createRandomKey();
    const publicKey = key.megolmV1PublicKey.publicKeyBase64;
    const { version } = await api<{ version: string }>('POST', '/e2ee/room-keys/version', {
      algorithm: BACKUP_ALGORITHM,
      auth_data: { public_key: publicKey },
    });
    await this.machine.saveBackupDecryptionKey(key, version);
    await this.machine.enableBackupV1(publicKey, version);
    await this.backup();
  }

  /** Resumes backing up if this device holds the key of the current backup. */
  private async enableExistingBackup() {
    const info = await this.currentBackup();
    const keys = await this.machine.getBackupKeys();
    const key = keys.decryptionKey;
    if (info && key && key.megolmV1PublicKey.publicKeyBase64 === info.auth_data.public_key) {
      await this.machine.enableBackupV1(info.auth_data.public_key, info.version);
    }
  }

  private async backup() {
    if (!(await this.machine.isBackupEnabled())) return;
    for (let i = 0; i < 50; i++) {
      const r = await this.machine.backupRoomKeys();
      if (!r) return;
      await this.send(r as unknown as Outgoing);
    }
  }

  /** After self-verification the backup key arrives as a secret: keep it and restore history. */
  private async receiveBackupSecret() {
    const secrets = await this.machine.getSecretsFromInbox(BACKUP_SECRET);
    const [secret] = [...secrets];
    if (!secret) return;
    await this.restoreWith(this.lib.BackupDecryptionKey.fromBase64(secret));
    await this.machine.deleteSecretsFromInbox(BACKUP_SECRET);
    await this.refreshVault();
  }

  private async restoreWith(key: CryptoModule.BackupDecryptionKey): Promise<number> {
    const info = await this.currentBackup();
    if (!info) throw new Error('no backup');
    if (key.megolmV1PublicKey.publicKeyBase64 !== info.auth_data.public_key) throw new Error('wrong recovery key');
    const { rooms } = await api<{
      rooms: Record<string, { sessions: Record<string, { session_data: { ephemeral: string; mac: string; ciphertext: string } }> }>;
    }>('GET', `/e2ee/room-keys/keys?version=${encodeURIComponent(info.version)}`);
    const map = new Map<CryptoModule.RoomId, Map<string, unknown>>();
    for (const [roomId, room] of Object.entries(rooms)) {
      const sessions = new Map<string, unknown>();
      for (const [sid, s] of Object.entries(room.sessions)) {
        try {
          sessions.set(sid, JSON.parse(key.decryptV1(s.session_data.ephemeral, s.session_data.mac, s.session_data.ciphertext)));
        } catch {
          /* one damaged entry must not stop the rest */
        }
      }
      map.set(new this.lib.RoomId(roomId), sessions);
    }
    const result = await this.machine.importBackedUpRoomKeys(map, () => undefined, info.version);
    await this.machine.saveBackupDecryptionKey(key, info.version);
    await this.machine.enableBackupV1(info.auth_data.public_key, info.version);
    this.plaintext.clear();
    this.changed();
    return result.importedCount;
  }

  /** Restores message history on this device from the recovery key. Returns the number of keys restored. */
  restoreFromRecoveryKey(recoveryKey: string): Promise<number> {
    return this.exclusive(() => this.restoreWith(this.lib.BackupDecryptionKey.fromBase64(recoveryKey.replace(/\s+/g, ''))));
  }

  // ---------- the vault (backup password) ----------

  /** What the vault would hold, if this device has all of it. */
  private async vaultContents(): Promise<VaultContents | null> {
    const backupKey = (await this.machine.getBackupKeys()).decryptionKey;
    const cs = await this.machine.exportCrossSigningKeys();
    if (!backupKey || !cs?.masterKey || !cs.self_signing_key || !cs.userSigningKey) return null;
    return { backupKey: backupKey.toBase64(), crossSigning: { master: cs.masterKey, selfSigning: cs.self_signing_key, userSigning: cs.userSigningKey } };
  }

  private async refreshVault() {
    const [{ vault }, contents] = await Promise.all([api<{ vault: VaultEnvelope | null }>('GET', '/e2ee/vault'), this.vaultContents()]);
    this.hasVault = vault !== null;
    this.canSealVault = contents !== null;
  }

  private async writeVault(password: string) {
    const contents = await this.vaultContents();
    if (!contents) throw new Error('keys missing');
    await api('PUT', '/e2ee/vault', await sealVault(contents, password));
    this.hasVault = true;
  }

  /** Sets (or changes) the backup password from a device that holds the keys. */
  setBackupPassword(password: string): Promise<void> {
    return this.exclusive(async () => {
      await this.writeVault(password);
      this.changed();
    });
  }

  /**
   * Links this device with the backup password: opens the vault, takes the identity's keys, signs this device
   * (so contacts trust it) and restores the history. Returns false when the password is wrong.
   */
  unlockWithPassword(password: string): Promise<boolean> {
    return this.exclusive(async () => {
      const { vault } = await api<{ vault: VaultEnvelope | null }>('GET', '/e2ee/vault');
      if (!vault) throw new Error('no vault');
      const contents = await openVault(vault, password);
      if (!contents) return false;
      const { master, selfSigning, userSigning } = contents.crossSigning;
      await this.machine.importCrossSigningKeys(master, selfSigning, userSigning);
      const device = await this.machine.getDevice(this.uid(this.userId), new this.lib.DeviceId(this.deviceId));
      if (device) await this.send((await device.verify()) as unknown as Outgoing);
      await this.flush();
      await this.restoreWith(this.lib.BackupDecryptionKey.fromBase64(contents.backupKey)).catch(() => 0);
      await this.refreshDeviceTrust();
      await this.refreshVault();
      this.changed();
      return true;
    });
  }

  // ---------- messages ----------

  /**
   * Encrypts for every signed device of the given people and of this user. Fails if someone we verified
   * changed their identity (the UI then asks the user to re-verify or accept the change).
   */
  encrypt(conversationId: string, peerIds: string[], text: string): Promise<MegolmContent> {
    return this.encryptContent(conversationId, peerIds, { msgtype: 'm.text', body: text });
  }

  /** Content for an encrypted upload: the file key travels inside the encrypted message only. */
  static fileContent(f: E2eeFileMeta): DecryptedContent {
    const msgtype = f.voice ? 'm.audio' : f.document ? 'm.file' : f.mime.startsWith('image/') ? 'm.image' : f.mime.startsWith('video/') ? 'm.video' : f.mime.startsWith('audio/') ? 'm.audio' : 'm.file';
    return {
      msgtype,
      body: f.name,
      info: { mimetype: f.mime, size: f.size, ...(f.durationMs ? { duration: f.durationMs } : {}), ...(f.round ? { round: true } : {}), ...(f.waveform?.length ? { waveform: f.waveform } : {}), ...(f.sticker ? { sticker: true } : {}), ...(f.thumb ? { thumbnail_data: f.thumb } : {}), ...(f.width && f.height ? { w: f.width, h: f.height } : {}) },
      file: { ...f.info, url: fileUri(f.attachmentId) },
      ...(f.caption ? { caption: f.caption } : {}),
    };
  }

  private forwardedDevices(): Record<string, string[]> {
    try {
      return JSON.parse(storeGet(FORWARDED_KEY(this.deviceId)) ?? '{}') as Record<string, string[]>;
    } catch {
      return {};
    }
  }

  /**
   * A device that joins a chat late (a person's new phone, or someone who signed up after the first messages
   * were sent) would never get the keys of what was said before: group keys are only ever shared forward.
   * So this device sends every device it has not served yet the keys it holds for the chat, Olm-encrypted,
   * device to device. Only devices signed by their owner's identity get them (the same rule as for new keys).
   */
  private async forwardHistory(conversationId: string, room: CryptoModule.RoomId, userIds: string[]): Promise<void> {
    const all = this.forwardedDevices();
    const done = new Set(all[conversationId] ?? []);
    const late: { userId: string; device: CryptoModule.Device }[] = [];
    for (const u of userIds) {
      const devices = await this.machine.getUserDevices(this.uid(u));
      for (const device of devices.devices()) {
        const id = device.deviceId.toString();
        const key = `${u}/${id}`;
        if (done.has(key) || (u === this.userId && id === this.deviceId)) continue;
        // Not signed by its owner yet: tried again on the next message.
        if (!device.isCrossSignedByOwner()) continue;
        late.push({ userId: u, device });
        done.add(key);
      }
    }
    if (late.length === 0) return;
    const roomId = room.toString();
    const exported = JSON.parse(await this.machine.exportRoomKeys((session) => session.roomId.toString() === roomId)) as unknown[];
    // Each device on its own: one the server refuses (no longer in a chat with us) is not asked again, one
    // that failed on the network is tried again later, and the others still get their keys.
    try {
      for (const { userId, device } of late) {
        const key = `${userId}/${device.deviceId.toString()}`;
        try {
          for (let i = 0; i < exported.length; i += FORWARD_BATCH) {
            const batch = exported.slice(i, i + FORWARD_BATCH);
            const encrypted = JSON.parse(await device.encryptToDeviceEvent(ROOM_KEYS_EVENT, { conversationId, keys: batch })) as Record<string, unknown>;
            await api('PUT', `/e2ee/send-to-device/m.room.encrypted/${encodeURIComponent(`fwd-${conversationId}-${Date.now()}-${i}-${device.deviceId.toString()}`)}`, {
              // Addressed like every to-device packet (the server reads "@id:server", never a bare id).
              messages: { [e2eeUserId(userId)]: { [device.deviceId.toString()]: encrypted } },
            });
          }
        } catch (err) {
          if (!(err instanceof ApiError && (err.code === 'FORBIDDEN' || err.code === 'NOT_FOUND'))) done.delete(key);
        }
      }
    } finally {
      all[conversationId] = [...done];
      storeSet(FORWARDED_KEY(this.deviceId), JSON.stringify(all));
    }
  }

  /** The other end of forwardHistory: keys from a device signed by its owner are taken in. Returns how many packets were used. */
  private async importForwardedKeys(events: CryptoModule.ProcessedToDeviceEvent[]): Promise<number> {
    let used = 0;
    for (const e of events) {
      if (e.type !== this.lib.ProcessedToDeviceEventType.Decrypted) continue;
      let raw: { type?: string; sender?: string; content?: { conversationId?: string; keys?: unknown[] } };
      try {
        raw = JSON.parse(e.rawEvent) as typeof raw;
      } catch {
        continue;
      }
      if (raw.type !== ROOM_KEYS_EVENT || !raw.sender || !Array.isArray(raw.content?.keys)) continue;
      const senderDevice = (e as CryptoModule.DecryptedToDeviceEvent).encryptionInfo.senderDevice;
      if (!senderDevice) continue;
      const device = await this.machine.getDevice(new this.lib.UserId(raw.sender), senderDevice);
      if (!device?.isCrossSignedByOwner()) continue;
      // Keys for other chats than the one named are not accepted.
      const keys = raw.content.keys.filter((k) => (k as { room_id?: string }).room_id === e2eeRoomId(raw.content?.conversationId ?? ''));
      if (!keys.length) continue;
      // A key sent by the very device that created the session (answering our request, or handing history on) is
      // as good as the original share: Olm proves the device, its owner signed it, and the session is its own.
      const deviceKey = device.curve25519Key?.toBase64();
      const owner = userIdFromE2ee(raw.sender);
      let marked = false;
      for (const k of keys as { sender_key?: unknown; session_id?: unknown }[]) {
        if (owner && deviceKey && k.sender_key === deviceKey && typeof k.session_id === 'string') {
          this.authentic.set(k.session_id, owner);
          marked = true;
        }
      }
      if (marked) writeAuthentic(this.deviceId, this.authentic);
      await this.machine.importExportedRoomKeys(JSON.stringify(keys), () => undefined);
      used += 1;
    }
    return used;
  }

  // ---------- missing keys: looking again, asking, answering ----------

  /** Whether a message that cannot be read is still being looked for ('waiting') or was not found ('failed'). */
  keyStatus(m: Pick<MessageDto, 'encrypted'>): KeyStatus | null {
    return m.encrypted ? this.recovery.status(m.encrypted.session_id) : null;
  }

  /** "Ask again" on a message that could not be decrypted. */
  requestKeyAgain(m: Pick<MessageDto, 'encrypted' | 'conversationId' | 'senderId'>): void {
    if (!m.encrypted) return;
    const sessionId = m.encrypted.session_id;
    if (!this.recovery.status(sessionId)) this.recovery.missing({ sessionId, conversationId: m.conversationId, senderId: m.senderId, senderDevice: m.encrypted.device_id ?? null });
    this.recovery.retryNow(sessionId);
  }

  /** Back online or in front again: searches that are due happen now. */
  nudgeKeySearch(): void {
    this.recovery.nudge();
  }

  /** One search for a missing key: pending packets, then my own key backup, then asking the devices. */
  private async searchKey(s: MissingSession, how: { round: number; manual: boolean }): Promise<void> {
    if (this.stopped) return;
    await this.sync().catch(() => undefined);
    if (!this.recovery.status(s.sessionId)) return;
    await this.exclusive(() => this.keyFromBackup(s)).catch(() => undefined);
    if (!this.recovery.status(s.sessionId)) return;
    // The first look is quick and quiet (the key is usually on its way); from then on the devices are asked too.
    if (how.round >= 1 || how.manual) await this.exclusive(() => this.requestKey(s));
  }

  /** My devices back up every key they get: this device takes the one it misses, when it holds the backup key. */
  private async keyFromBackup(s: MissingSession): Promise<void> {
    if (this.parked || !(await this.machine.isBackupEnabled())) return;
    const { decryptionKey: key, backupVersion: version } = await this.machine.getBackupKeys();
    if (!key || !version) return;
    const roomId = e2eeRoomId(s.conversationId);
    let data: { session_data: { ephemeral: string; mac: string; ciphertext: string } };
    try {
      data = await api('GET', `/e2ee/room-keys/keys/${encodeURIComponent(roomId)}/${encodeURIComponent(s.sessionId)}?version=${encodeURIComponent(version)}`);
    } catch {
      return; // not backed up (yet), or no network
    }
    const session = JSON.parse(key.decryptV1(data.session_data.ephemeral, data.session_data.mac, data.session_data.ciphertext)) as unknown;
    await this.machine.importBackedUpRoomKeys(new Map([[new this.lib.RoomId(roomId), new Map([[s.sessionId, session]])]]), () => undefined, version);
  }

  /**
   * Asks the device that wrote the message (all of the sender's devices when it is not named) and my own other
   * devices for the key, Olm-encrypted. Only devices signed by their owner are asked.
   */
  private async requestKey(s: MissingSession): Promise<void> {
    if (this.parked) return;
    const roomId = e2eeRoomId(s.conversationId);
    const users = [...new Set([s.senderId, this.userId])];
    await this.track(users);
    const claim = await this.machine.getMissingSessions(users.map((u) => this.uid(u)));
    if (claim) await this.send(claim as Outgoing);
    const messages: Record<string, Record<string, unknown>> = {};
    for (const u of users) {
      const signed = (await this.machine.getUserDevices(this.uid(u))).devices().filter((d) => d.isCrossSignedByOwner() && !(u === this.userId && d.deviceId.toString() === this.deviceId));
      let targets = signed;
      if (u === s.senderId && u !== this.userId && s.senderDevice) {
        const named = signed.filter((d) => d.deviceId.toString() === s.senderDevice);
        if (named.length) targets = named;
      }
      for (const d of targets) {
        const encrypted = JSON.parse(await d.encryptToDeviceEvent(KEY_REQUEST_EVENT, { conversationId: s.conversationId, room_id: roomId, session_id: s.sessionId })) as Record<string, unknown>;
        (messages[e2eeUserId(u)] ??= {})[d.deviceId.toString()] = encrypted;
      }
    }
    if (!Object.keys(messages).length) return;
    await api('PUT', `/e2ee/send-to-device/m.room.encrypted/${encodeURIComponent(`req-${s.sessionId.slice(0, 12)}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)}`, { messages });
  }

  /**
   * Answers key requests the way Matrix does: my own signed devices get any key I hold; another person's signed
   * device gets a key only when this device created that session (it wrote those messages) and the person is a
   * member of the chat (whom it was shared with in the first place). Nobody else gets anything.
   */
  private async answerKeyRequests(events: CryptoModule.ProcessedToDeviceEvent[]): Promise<void> {
    for (const e of events) {
      if (e.type !== this.lib.ProcessedToDeviceEventType.Decrypted) continue;
      let raw: { type?: string; sender?: string; content?: { conversationId?: unknown; room_id?: unknown; session_id?: unknown } };
      try {
        raw = JSON.parse(e.rawEvent) as typeof raw;
      } catch {
        continue;
      }
      if (raw.type !== KEY_REQUEST_EVENT || !raw.sender || !raw.content) continue;
      const { conversationId, room_id: roomId, session_id: sessionId } = raw.content;
      if (typeof conversationId !== 'string' || typeof sessionId !== 'string' || roomId !== e2eeRoomId(conversationId)) continue;
      const requester = userIdFromE2ee(raw.sender);
      const senderDevice = (e as CryptoModule.DecryptedToDeviceEvent).encryptionInfo.senderDevice;
      if (!requester || !senderDevice) continue;
      const own = requester === this.userId;
      if (own && senderDevice.toString() === this.deviceId) continue;
      const device = await this.machine.getDevice(new this.lib.UserId(raw.sender), senderDevice);
      if (!device?.isCrossSignedByOwner()) continue;
      const seen = `${requester}/${senderDevice.toString()}/${sessionId}`;
      if (Date.now() - (this.answered.get(seen) ?? 0) < ANSWER_EVERY_MS) continue;
      const keys = JSON.parse(await this.machine.exportRoomKeys((k) => k.roomId.toString() === roomId && k.sessionId === sessionId)) as { sender_key?: unknown }[];
      if (!keys.length) continue;
      if (!own) {
        if (keys[0]?.sender_key !== this.machine.identityKeys.curve25519.toBase64()) continue;
        if (!this.memberCheck || !(await this.memberCheck(conversationId, requester).catch(() => false))) continue;
      }
      this.answered.set(seen, Date.now());
      const encrypted = JSON.parse(await device.encryptToDeviceEvent(ROOM_KEYS_EVENT, { conversationId, keys })) as Record<string, unknown>;
      await api('PUT', `/e2ee/send-to-device/m.room.encrypted/${encodeURIComponent(`ans-${sessionId.slice(0, 12)}-${Date.now()}-${senderDevice.toString()}`)}`, {
        messages: { [raw.sender]: { [senderDevice.toString()]: encrypted } },
      });
    }
  }

  encryptContent(conversationId: string, recipientIds: string[], body: DecryptedContent): Promise<MegolmContent> {
    return this.exclusive(async () => {
      const { RoomId, EncryptionSettings, CollectStrategy } = this.lib;
      const ids = [...new Set([this.userId, ...recipientIds])];
      const users = () => ids.map((u) => this.uid(u));
      const room = new RoomId(e2eeRoomId(conversationId));
      // Someone left (or this is the first message since the app started): start a new group session,
      // so a removed member cannot read what follows.
      const previous = this.sessionMembers.get(conversationId);
      if (!previous || [...previous].some((u) => !ids.includes(u))) await this.machine.invalidateGroupSession(room);
      this.sessionMembers.set(conversationId, new Set(ids));
      await this.track(ids);
      const claim = await this.machine.getMissingSessions(users());
      if (claim) await this.send(claim as Outgoing);
      const settings = new EncryptionSettings();
      settings.sharingStrategy = CollectStrategy.identityBasedStrategy();
      let shares;
      try {
        shares = await this.machine.shareRoomKey(room, users(), settings);
      } catch (err) {
        // A half-made session must not be reused (the retry would encrypt with a session whose key this
        // device never stored): start over on the next attempt.
        await this.machine.invalidateGroupSession(room);
        this.sessionMembers.delete(conversationId);
        // The library refuses when someone we verified changed their identity.
        for (const u of ids) if ((await this.trustOfInner(u)) === 'changed') throw new ApiError('E2EE_IDENTITY_CHANGED', 0);
        throw err;
      }
      for (const r of shares) await this.send(r as Outgoing);
      const content = await this.machine.encryptRoomEvent(room, 'm.room.message', JSON.stringify(body));
      await this.backup();
      // Older keys for devices that joined late go out after this message, never before it: sending stays quick.
      void this.exclusive(() => this.forwardHistory(conversationId, room, ids)).catch(() => undefined);
      // The library copies a relation (a reaction's "m.relates_to") out in the clear next to the ciphertext,
      // as Matrix servers expect. Ours does not need it and refuses unknown fields: only the ciphertext goes.
      const { algorithm, ciphertext, session_id, sender_key, device_id } = JSON.parse(content) as MegolmContent;
      return { algorithm, ciphertext, session_id, ...(sender_key ? { sender_key } : {}), ...(device_id ? { device_id } : {}) };
    });
  }

  /**
   * The message text, or null when this device has no key for it (yet). Messages from devices that are
   * not signed by their owner are only shown when the key came from our own backup (history), and are
   * then flagged `unverifiedSender`.
   */
  /** Encrypts a file on this device (AES-CTR with integrity hash, done by the crypto library). */
  encryptFile(bytes: Uint8Array): { data: Uint8Array; info: Record<string, unknown> } {
    const enc = this.lib.Attachment.encrypt(bytes);
    const info = JSON.parse(enc.mediaEncryptionInfo ?? '{}') as Record<string, unknown>;
    return { data: enc.encryptedData, info };
  }

  /** Decrypts a downloaded file; throws if the ciphertext was altered. */
  decryptFile(bytes: Uint8Array, file: Record<string, unknown>): Uint8Array {
    // The library expects the EncryptedFile info without our attachment URI.
    const info = Object.fromEntries(Object.entries(file).filter(([k]) => k !== 'url'));
    return this.lib.Attachment.decrypt(new this.lib.EncryptedAttachment(bytes, JSON.stringify(info)));
  }

  decrypt(m: MessageDto): Promise<DecryptedMessage | null> {
    if (!m.encrypted) return Promise.resolve(null);
    // An edit brings new ciphertext under the same id: it is decrypted again.
    const cacheKey = `${m.id}:${m.editedAt ?? ''}`;
    let p = this.plaintext.get(cacheKey);
    if (!p) {
      const encrypted = m.encrypted;
      p = this.exclusive(async () => {
        const { RoomId, DecryptionSettings, TrustRequirement } = this.lib;
        const room = new RoomId(e2eeRoomId(m.conversationId));
        await this.track([m.senderId]);
        const event = JSON.stringify({
          type: 'm.room.encrypted',
          event_id: `$${m.id}`,
          sender: e2eeUserId(m.senderId),
          origin_server_ts: Date.parse(m.createdAt),
          room_id: room.toString(),
          content: encrypted,
        });
        const read = async (trust: number) => {
          const dec = await this.machine.decryptRoomEvent(event, room, new DecryptionSettings(trust));
          const content = (JSON.parse(dec.event) as { content?: DecryptedContent }).content;
          return content && typeof content.body === 'string' ? content : null;
        };
        try {
          const content = await read(TrustRequirement.CrossSignedOrLegacy);
          return content && { content, unverifiedSender: false };
        } catch {
          try {
            const content = await read(TrustRequirement.Untrusted);
            // The key came from the sender's own signed device (on request, see importForwardedKeys): no warning.
            return content && { content, unverifiedSender: this.authentic.get(encrypted.session_id) !== m.senderId };
          } catch (err) {
            if (isMissingKey(err, this.lib)) {
              // Not here yet: shown as waiting and looked for; decrypted again as soon as the key arrives.
              let keys = this.missingBySession.get(encrypted.session_id);
              if (!keys) this.missingBySession.set(encrypted.session_id, (keys = new Set()));
              keys.add(cacheKey);
              this.recovery.missing({ sessionId: encrypted.session_id, conversationId: m.conversationId, senderId: m.senderId, senderDevice: encrypted.device_id ?? null });
            }
            return null;
          }
        }
      });
      this.plaintext.set(cacheKey, p);
    }
    return p;
  }

  // ---------- reactions ----------

  /** My reaction to a message, encrypted for everyone in the chat (an annotation pointing at it). */
  encryptReaction(conversationId: string, recipientIds: string[], messageId: string, emoji: string): Promise<MegolmContent> {
    return this.encryptContent(conversationId, recipientIds, {
      msgtype: REACTION_TYPE,
      body: emoji,
      'm.relates_to': { rel_type: 'm.annotation', event_id: `$${messageId}` },
    });
  }

  /** My vote on a poll or answer to an event, encrypted for everyone in the chat. */
  encryptAnswer(conversationId: string, recipientIds: string[], messageId: string, answer: EncryptedAnswer): Promise<MegolmContent> {
    return this.encryptContent(conversationId, recipientIds, {
      msgtype: ANSWER_TYPE,
      body: '',
      answer,
      'm.relates_to': { rel_type: 'm.reference', event_id: `$${messageId}` },
    });
  }

  private answers = new Map<string, Promise<EncryptedAnswer | null>>();

  /**
   * One person's answer to a poll or event, or null. As with reactions, only accepted from that person's
   * own device and only for this message; anything malformed is ignored.
   */
  decryptAnswer(conversationId: string, messageId: string, userId: string, encrypted: MegolmContent): Promise<EncryptedAnswer | null> {
    const cacheKey = `${messageId}:${userId}:${encrypted.session_id}:${encrypted.ciphertext.slice(0, 32)}`;
    let p = this.answers.get(cacheKey);
    if (!p) {
      p = this.exclusive(async () => {
        const { RoomId, DecryptionSettings, TrustRequirement } = this.lib;
        const room = new RoomId(e2eeRoomId(conversationId));
        await this.track([userId]);
        const event = JSON.stringify({
          type: 'm.room.encrypted',
          event_id: `$${messageId}~a~${userId}~${encrypted.ciphertext.slice(0, 16)}`,
          sender: e2eeUserId(userId),
          origin_server_ts: 0,
          room_id: room.toString(),
          content: encrypted,
        });
        try {
          const dec = await this.machine.decryptRoomEvent(event, room, new DecryptionSettings(TrustRequirement.CrossSignedOrLegacy));
          const c = (JSON.parse(dec.event) as { content?: DecryptedContent }).content;
          if (c?.msgtype !== ANSWER_TYPE || c['m.relates_to']?.event_id !== `$${messageId}` || !c.answer || typeof c.answer !== 'object') return null;
          const a = c.answer;
          const vote = Array.isArray(a.vote) ? a.vote.filter((x): x is number => Number.isInteger(x) && x >= 0 && x < 64).slice(0, 64) : undefined;
          const rsvp = a.rsvp === 'going' || a.rsvp === 'maybe' || a.rsvp === 'no' ? a.rsvp : undefined;
          return { ...(vote ? { vote } : {}), ...(rsvp ? { rsvp } : {}), ...(a.closed === true ? { closed: true } : {}) };
        } catch {
          return null;
        }
      });
      void p.then((v) => v === null && this.answers.delete(cacheKey));
      this.answers.set(cacheKey, p);
    }
    return p;
  }

  private reactions = new Map<string, Promise<string | null>>();

  /**
   * The emoji of one person's encrypted reaction, or null. Only accepted from a device that belongs to that
   * person (the library refuses a ciphertext passed off as someone else's), and only for this message.
   */
  decryptReaction(conversationId: string, messageId: string, userId: string, encrypted: MegolmContent): Promise<string | null> {
    const cacheKey = `${messageId}:${userId}:${encrypted.session_id}:${encrypted.ciphertext.slice(0, 32)}`;
    let p = this.reactions.get(cacheKey);
    if (!p) {
      p = this.exclusive(async () => {
        const { RoomId, DecryptionSettings, TrustRequirement } = this.lib;
        const room = new RoomId(e2eeRoomId(conversationId));
        await this.track([userId]);
        const event = JSON.stringify({
          type: 'm.room.encrypted',
          event_id: `$${messageId}~${userId}~${encrypted.ciphertext.slice(0, 16)}`,
          sender: e2eeUserId(userId),
          origin_server_ts: 0, // stable: the same ciphertext always describes the same event
          room_id: room.toString(),
          content: encrypted,
        });
        try {
          const dec = await this.machine.decryptRoomEvent(event, room, new DecryptionSettings(TrustRequirement.CrossSignedOrLegacy));
          const c = (JSON.parse(dec.event) as { content?: DecryptedContent }).content;
          const emoji = c?.body;
          const ok = c?.msgtype === REACTION_TYPE && c['m.relates_to']?.event_id === `$${messageId}` && typeof emoji === 'string' && emoji.length > 0 && emoji.length <= MAX_EMOJI_LENGTH;
          return ok ? emoji : null;
        } catch {
          return null;
        }
      });
      // A key that arrives later should get another chance: failures are not kept.
      void p.then((v) => v === null && this.reactions.delete(cacheKey));
      this.reactions.set(cacheKey, p);
    }
    return p;
  }
}

const REACTION_TYPE = 'm.dijitalapp.reaction';

/** The library's "no key for this message (yet)": the room key is missing, or only known from a later message on. */
function isMissingKey(err: unknown, lib: Crypto): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return code === lib.DecryptionErrorCode.MissingRoomKey || code === lib.DecryptionErrorCode.UnknownMessageIndex;
}

/** Sessions whose key came from their creator's own signed device (exported for tests). */
export function readAuthentic(deviceId: string): Map<string, string> {
  try {
    const list = JSON.parse(storeGet(AUTHENTIC_KEY(deviceId)) ?? '[]') as unknown;
    return new Map(Array.isArray(list) ? list.filter((x): x is [string, string] => Array.isArray(x) && typeof x[0] === 'string' && typeof x[1] === 'string') : []);
  } catch {
    return new Map();
  }
}
export function writeAuthentic(deviceId: string, map: Map<string, string>) {
  storeSet(AUTHENTIC_KEY(deviceId), JSON.stringify([...map.entries()].slice(-AUTHENTIC_MAX)));
}

/** The QR code as a square grid of modules (1 = dark). */
function qrGrid(qr: CryptoModule.Qr): { size: number; modules: Uint8ClampedArray } {
  const modules = qr.toQrCode().renderIntoBuffer();
  return { size: Math.round(Math.sqrt(modules.length)), modules };
}

/**
 * The crypto library reports problems as English text. They become coded errors, so the app shows a message
 * in the person's language with what to do next (never the library's raw text).
 */
export function cryptoError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const text = err instanceof Error ? err.message : String(err);
  if (/signing key is missing|MissingSigningKey|no (user|self)[- ]signing key|private key.*(missing|not available)/i.test(text)) {
    return new ApiError('E2EE_SIGNING_KEY_MISSING', 0);
  }
  if (/network|fetch|failed to fetch/i.test(text)) return new ApiError('NETWORK', 0);
  return new ApiError('E2EE_SEND_FAILED', 0);
}
